// Feature: features/nma-claims-known-issues-tabs.md, features/nma-local-discovery-streamlit-console.md
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Select,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Badge,
  Code,
  HStack,
  VStack,
  Divider,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

const DEFAULT_SUPPORTED_CPTS = [
  '95999',
  '95938',
  '95939',
  '95940',
  '95941',
  'G0453',
  '95822',
  '95955',
  '95861',
  'ALL_CLAIMS',
];

const StageSmallMultipleCard = React.memo(({
  rank,
  entityName,
  totalDenom,
  totalNum,
  totalNum2,
  weeklySeries,
  rateLabel,
  rate2Label,
  lineColor,
  line2Color,
  barColor,
  rowLabel,
  stageId,
  groupType,
}) => {
  const overallPct = totalDenom > 0 ? ((totalNum / totalDenom) * 100).toFixed(1) : '0.0';
  const overallPct2 =
    totalNum2 !== null && totalNum2 !== undefined && totalDenom > 0
      ? ((totalNum2 / totalDenom) * 100).toFixed(1)
      : null;
  const hasSecondary = Boolean(rate2Label);
  const syncId = `sync_${stageId}_${groupType}_${rank}`;

  return (
    <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
      <CardBody p={3}>
        {/* Card Header */}
        <Box mb={2}>
          <Text fontSize="xs" fontWeight="bold" color="gray.900" noOfLines={1} title={entityName}>
            #{rank} {entityName}
          </Text>
          {hasSecondary ? (
            <Box mt={0.5}>
              <Text fontSize="11px" color="gray.700">
                EOB Volume: <b>{totalDenom.toLocaleString()} claims</b>
              </Text>
              <HStack spacing={2} mt={0.5} wrap="wrap">
                <Text fontSize="11px" color="orange.700" fontWeight="semibold">
                  ● {rowLabel} Denied: <b>{overallPct}%</b> ({totalNum.toLocaleString()})
                </Text>
                <Text fontSize="11px" color="gray.400">
                  |
                </Text>
                <Text fontSize="11px" color="green.700" fontWeight="semibold">
                  ● {rowLabel} Approved: <b>{overallPct2}%</b> ({(totalNum2 || 0).toLocaleString()})
                </Text>
              </HStack>
            </Box>
          ) : (
            <HStack spacing={2} mt={0.5} wrap="wrap">
              <Text fontSize="11px" color="gray.700">
                Total Volume: <b>{totalDenom.toLocaleString()} claims</b>
              </Text>
              <Text fontSize="11px" color="gray.400">
                |
              </Text>
              <Text fontSize="11px" color="gray.900" fontWeight="semibold">
                {rateLabel}: <b>{overallPct}%</b> ({totalNum.toLocaleString()} / {totalDenom.toLocaleString()})
              </Text>
            </HStack>
          )}
        </Box>

        {/* Top Panel: % Rate Line(s) (0 - 100%) */}
        <Box h="140px" w="100%">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={weeklySeries}
              syncId={syncId}
              margin={{ top: 6, right: 10, left: -18, bottom: 2 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week" hide />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tick={{ fontSize: 10, fill: '#1A202C' }}
                unit="%"
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: '#1A202C',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '11px',
                  padding: '6px 10px',
                }}
                labelStyle={{ color: '#E2E8F0', fontWeight: 'bold', marginBottom: '2px' }}
                formatter={(val, name, props) => {
                  const p = props?.payload || {};
                  if (name === rate2Label) {
                    return [
                      val !== null && val !== undefined
                        ? `${val}% (${p.num2 || 0} / ${p.denom || 0} claims)`
                        : 'No volume',
                      name,
                    ];
                  }
                  return [
                    val !== null && val !== undefined
                      ? `${val}% (${p.num || 0} / ${p.denom || 0} claims)`
                      : 'No volume',
                    name,
                  ];
                }}
                labelFormatter={(wk) => `Week of ${wk}`}
              />
              <Line
                type="monotone"
                dataKey="ratePct"
                name={rateLabel}
                stroke={lineColor}
                strokeWidth={2.2}
                dot={{ r: 3, fill: lineColor }}
                activeDot={{ r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
              {hasSecondary && (
                <Line
                  type="monotone"
                  dataKey="rate2Pct"
                  name={rate2Label}
                  stroke={line2Color || '#059669'}
                  strokeWidth={2.2}
                  dot={{ r: 3, fill: line2Color || '#059669' }}
                  activeDot={{ r: 5 }}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* Bottom Panel: Weekly Claim Volume Bars Underneath */}
        <Box h="92px" w="100%" mt={1}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={weeklySeries}
              syncId={syncId}
              margin={{ top: 2, right: 10, left: -18, bottom: 18 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 9, fill: '#1A202C' }}
                angle={-30}
                textAnchor="end"
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10, fill: '#1A202C' }}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: '#1A202C',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '11px',
                  padding: '5px 8px',
                }}
                labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                formatter={(val) => [`${val} claims`, 'Weekly Volume']}
                labelFormatter={(wk) => `Week of ${wk}`}
              />
              <Bar
                dataKey="denom"
                name="Weekly Volume"
                fill={barColor}
                stroke={hasSecondary ? '#64748B' : lineColor}
                strokeWidth={0.8}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardBody>
    </Card>
  );
});

const NMAVisualDiscoveryTab = React.memo(({ data, initialCpt }) => {
  const discoveryData = data || { supported_cpts: DEFAULT_SUPPORTED_CPTS, claims: [] };
  const supportedCpts = discoveryData.supported_cpts || DEFAULT_SUPPORTED_CPTS;
  const rawClaims = discoveryData.claims || [];

  // Filter & layout controls (matching Streamlit sidebar defaults)
  const [targetCpt, setTargetCpt] = useState(
    initialCpt && supportedCpts.includes(initialCpt) ? initialCpt : '95999'
  );

  useEffect(() => {
    if (initialCpt && supportedCpts.includes(initialCpt)) {
      setTargetCpt(initialCpt);
    }
  }, [initialCpt, supportedCpts]);
  const [stageViewFilter, setStageViewFilter] = useState('ALL_STAGES');
  const [entityLevelMode, setEntityLevelMode] = useState('BOTH');
  const [claimTypeFilter, setClaimTypeFilter] = useState('ALL');
  const [weekAxisMode, setWeekAxisMode] = useState('dw'); // 'dw' = DOS week, 'rw' = Response week
  const [minWeekFilter, setMinWeekFilter] = useState('');
  const [maxWeekFilter, setMaxWeekFilter] = useState('');
  const [colsPerRow, setColsPerRow] = useState(3);
  const [topNCarriers, setTopNCarriers] = useState(30);

  // Stage-specific scope submode toggles (defaulting to CPT-specific / finalized EOBs)
  const [stage1Submode, setStage1Submode] = useState('CPT_SPECIFIC_ONLY');
  const [stage2Submode, setStage2Submode] = useState('STRICT_CPT_HOLD');
  const [stage3Submode, setStage3Submode] = useState('FINALIZED_EOBS_ONLY');

  const cptLabel = targetCpt !== 'ALL_CLAIMS' ? `CPT ${targetCpt}` : 'Claim';
  const rowLabel = targetCpt !== 'ALL_CLAIMS' ? `${targetCpt} Row` : 'Claim';

  // Project claims for the selected Target CPT & Claim Type
  const baseScopedClaims = useMemo(() => {
    const out = [];
    for (let i = 0; i < rawClaims.length; i += 1) {
      const c = rawClaims[i];
      if (claimTypeFilter !== 'ALL' && c.ct !== claimTypeFilter) {
        continue;
      }
      const wk = c[weekAxisMode];
      if (!wk) {
        continue;
      }
      const flags = targetCpt === 'ALL_CLAIMS' ? c.all : c.cpts?.[targetCpt];
      if (!flags) {
        continue;
      }
      // flags layout:
      // [0:ch_cpt, 1:hold_strict, 2:hold_eob, 3:hold_whole, 4:eob_adj, 5:eob_fin, 6:eob_den_fin, 7:eob_den_all, 8:eob_paid]
      const chAny = c.all ? c.all[0] : flags[0];
      const recAny = c.all ? c.all[1] : flags[3];
      out.push({
        cn: c.cn,
        ct: c.ct,
        pg: c.pg || 'OTHER PAYERS',
        cr: c.cr || 'Unknown Payer',
        week: wk,
        isSubmitted: 1,
        chCpt: flags[0] || 0,
        chAny: chAny || 0,
        holdStrict: flags[1] || 0,
        holdEob: flags[2] || 0,
        holdWhole: flags[3] || 0,
        holdAny: recAny || flags[1] || 0,
        eobAdj: flags[4] || 0,
        eobFin: flags[5] || 0,
        eobDenFin: flags[6] || 0,
        eobDenAll: flags[7] || 0,
        eobPaid: flags[8] || 0,
      });
    }
    return out;
  }, [rawClaims, targetCpt, claimTypeFilter, weekAxisMode]);

  const allAvailableWeeks = useMemo(() => {
    const wkSet = new Set();
    for (let i = 0; i < baseScopedClaims.length; i += 1) {
      wkSet.add(baseScopedClaims[i].week);
    }
    return Array.from(wkSet).sort();
  }, [baseScopedClaims]);

  const defaultStartWeek = useMemo(
    () => allAvailableWeeks.find((w) => w >= '2026-07') || allAvailableWeeks[0] || '',
    [allAvailableWeeks]
  );

  const effectiveMinWeek = minWeekFilter && allAvailableWeeks.includes(minWeekFilter)
    ? minWeekFilter
    : defaultStartWeek;
  const effectiveMaxWeek = maxWeekFilter && allAvailableWeeks.includes(maxWeekFilter)
    ? maxWeekFilter
    : allAvailableWeeks[allAvailableWeeks.length - 1] || '';

  const activeWeeks = useMemo(
    () => allAvailableWeeks.filter((w) => w >= effectiveMinWeek && w <= effectiveMaxWeek),
    [allAvailableWeeks, effectiveMinWeek, effectiveMaxWeek]
  );

  const filteredClaims = useMemo(
    () => baseScopedClaims.filter((c) => c.week >= effectiveMinWeek && c.week <= effectiveMaxWeek),
    [baseScopedClaims, effectiveMinWeek, effectiveMaxWeek]
  );

  // Executive KPI Totals
  const kpiTotals = useMemo(() => {
    let sub = 0;
    let chCpt = 0;
    let chAny = 0;
    let holdStrict = 0;
    let holdEob = 0;
    let eobFin = 0;
    let eobDenFin = 0;
    let eobPaid = 0;
    const payerSet = new Set();
    const carrierSet = new Set();

    for (let i = 0; i < filteredClaims.length; i += 1) {
      const r = filteredClaims[i];
      sub += 1;
      chCpt += r.chCpt;
      chAny += r.chAny;
      holdStrict += r.holdStrict;
      holdEob += r.holdEob;
      eobFin += r.eobFin;
      eobDenFin += r.eobDenFin;
      eobPaid += r.eobPaid;
      payerSet.add(r.pg);
      carrierSet.add(r.cr);
    }
    return {
      sub,
      chCpt,
      chAny,
      holdStrict,
      holdEob,
      eobFin,
      eobDenFin,
      eobPaid,
      payersCount: payerSet.size,
      carriersCount: carrierSet.size,
    };
  }, [filteredClaims]);

  // Helper to build sorted Payer Group & Top N Carrier series for a stage
  const buildStageEntities = (denomField, numField, num2Field, maxEntities) => {
    const byEntity = new Map();
    for (let i = 0; i < filteredClaims.length; i += 1) {
      const r = filteredClaims[i];
      if (!r[denomField]) {
        continue;
      }
      const dVal = r[denomField] ? 1 : 0;
      const nVal = r[numField] ? 1 : 0;
      const n2Val = num2Field && r[num2Field] ? 1 : 0;

      const updateGroup = (mapObj, key) => {
        let entry = mapObj.get(key);
        if (!entry) {
          entry = {
            entityName: key,
            totalDenom: 0,
            totalNum: 0,
            totalNum2: num2Field ? 0 : null,
            byWeek: new Map(),
          };
          mapObj.set(key, entry);
        }
        entry.totalDenom += dVal;
        entry.totalNum += nVal;
        if (num2Field) {
          entry.totalNum2 += n2Val;
        }
        let wkEntry = entry.byWeek.get(r.week);
        if (!wkEntry) {
          wkEntry = { denom: 0, num: 0, num2: 0 };
          entry.byWeek.set(r.week, wkEntry);
        }
        wkEntry.denom += dVal;
        wkEntry.num += nVal;
        if (num2Field) {
          wkEntry.num2 += n2Val;
        }
      };

      updateGroup(byEntity, r._groupKey);
    }

    const sorted = Array.from(byEntity.values())
      .sort((a, b) => b.totalDenom - a.totalDenom || b.totalNum - a.totalNum)
      .slice(0, maxEntities);

    return sorted.map((ent) => ({
      entityName: ent.entityName,
      totalDenom: ent.totalDenom,
      totalNum: ent.totalNum,
      totalNum2: ent.totalNum2,
      weeklySeries: activeWeeks.map((wk) => {
        const w = ent.byWeek.get(wk);
        const denom = w ? w.denom : 0;
        const num = w ? w.num : 0;
        const num2 = w ? w.num2 : 0;
        return {
          week: wk,
          denom,
          num,
          num2,
          ratePct: denom > 0 ? Number(((num / denom) * 100).toFixed(1)) : null,
          rate2Pct: num2Field && denom > 0 ? Number(((num2 / denom) * 100).toFixed(1)) : null,
        };
      }),
    }));
  };

  const buildPayerAndCarrierGrids = (denomField, numField, num2Field) => {
    for (let i = 0; i < filteredClaims.length; i += 1) {
      filteredClaims[i]._groupKey = filteredClaims[i].pg;
    }
    const payerEntities = buildStageEntities(denomField, numField, num2Field, 20);

    for (let i = 0; i < filteredClaims.length; i += 1) {
      filteredClaims[i]._groupKey = filteredClaims[i].cr;
    }
    const carrierEntities = buildStageEntities(denomField, numField, num2Field, topNCarriers);

    return { payerEntities, carrierEntities };
  };

  // Stage 1 resolved fields
  const stage1NumField =
    stage1Submode === 'ALL_HEADER_REJECTIONS' ? 'chAny' : 'chCpt';
  const stage1RateLabel =
    stage1Submode === 'ALL_HEADER_REJECTIONS'
      ? 'All 277CA Rejected (Incl. NPI/Sub) %'
      : `${rowLabel} 277CA Rejected %`;

  // Stage 2 resolved fields
  const stage2NumField =
    stage2Submode === 'EOB_LINE_HOLD_ONLY'
      ? 'holdEob'
      : stage2Submode === 'INCL_WHOLE_CLAIM_LETTER'
      ? 'holdWhole'
      : stage2Submode === 'ALL_CLAIM_HOLDS'
      ? 'holdAny'
      : 'holdStrict';
  const stage2RateLabel =
    stage2Submode === 'EOB_LINE_HOLD_ONLY'
      ? `${rowLabel} EOB Hold (252/M127) %`
      : stage2Submode === 'INCL_WHOLE_CLAIM_LETTER'
      ? `${rowLabel} Hold + Whole-Claim Letter %`
      : stage2Submode === 'ALL_CLAIM_HOLDS'
      ? 'All Claim Record Holds %'
      : `Strict ${rowLabel} Record Hold %`;

  // Stage 3 resolved fields
  const stage3DenomField =
    stage3Submode === 'ALL_EOBS_INCL_252_HOLDS' ? 'eobAdj' : 'eobFin';
  const stage3NumField =
    stage3Submode === 'ALL_EOBS_INCL_252_HOLDS' ? 'eobDenAll' : 'eobDenFin';
  const stage3RateLabel =
    stage3Submode === 'ALL_EOBS_INCL_252_HOLDS'
      ? `${rowLabel} EOB Denied (Incl. 252) %`
      : `${rowLabel} EOB Denied %`;
  const stage3Rate2Label = `${rowLabel} EOB Approved %`;

  const stage1Grids = useMemo(
    () => buildPayerAndCarrierGrids('isSubmitted', stage1NumField, null),
    [filteredClaims, activeWeeks, stage1NumField, topNCarriers]
  );
  const stage2Grids = useMemo(
    () => buildPayerAndCarrierGrids('isSubmitted', stage2NumField, null),
    [filteredClaims, activeWeeks, stage2NumField, topNCarriers]
  );
  const stage3Grids = useMemo(
    () => buildPayerAndCarrierGrids(stage3DenomField, stage3NumField, 'eobPaid'),
    [filteredClaims, activeWeeks, stage3DenomField, stage3NumField, topNCarriers]
  );

  return (
    <VStack spacing={6} align="stretch">
      {/* TOP CONTROL BAR (MATCHING STREAMLIT SIDEBAR) */}
      <Card variant="outline" bg="gray.50" borderColor="blue.200">
        <CardBody py={3} px={4}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={3}>
            <Box>
              <HStack spacing={2}>
                <Badge colorScheme="blue" variant="solid" px={2} py={0.5}>
                  VISUAL STAGE DISCOVERY
                </Badge>
                <Heading size="sm" color="gray.800">
                  CPT {targetCpt} Lifecycle Stage % & Weekly Volume — By Payer Group & Top {topNCarriers} Carriers
                </Heading>
              </HStack>
              <Text fontSize="xs" color="gray.600" mt={0.5}>
                Each card synchronizes the <b>Weekly Stage % Line(s) (0–100%) on top</b> with the <b>Weekly Claim Volume Bars underneath</b>, sorted by <b>Total Volume Descending</b>.
              </Text>
            </Box>
          </Flex>

          <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={3}>
            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                Target CPT Scope
              </Text>
              <Select
                size="xs"
                bg="white"
                fontWeight="bold"
                value={targetCpt}
                onChange={(e) => setTargetCpt(e.target.value)}
              >
                {supportedCpts.map((c) => (
                  <option key={c} value={c}>
                    {c === 'ALL_CLAIMS' ? 'ALL CLAIMS' : `CPT ${c}`}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                Stage Section View
              </Text>
              <Select
                size="xs"
                bg="white"
                value={stageViewFilter}
                onChange={(e) => setStageViewFilter(e.target.value)}
              >
                <option value="ALL_STAGES">All 3 Stages (Scroll)</option>
                <option value="STAGE_1">Stage 1: 277CA Rejections</option>
                <option value="STAGE_2">Stage 2: Record Holds</option>
                <option value="STAGE_3">Stage 3: EOB Denied vs. Approved</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                Entity Breakdown
              </Text>
              <Select
                size="xs"
                bg="white"
                value={entityLevelMode}
                onChange={(e) => setEntityLevelMode(e.target.value)}
              >
                <option value="BOTH">Payer Groups & Top Carriers</option>
                <option value="PAYERS_ONLY">Payer Groups Only</option>
                <option value="CARRIERS_ONLY">Top Carriers Only</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                Claim Type Filter
              </Text>
              <Select
                size="xs"
                bg="white"
                value={claimTypeFilter}
                onChange={(e) => setClaimTypeFilter(e.target.value)}
              >
                <option value="ALL">All Claims (Pro & Tech)</option>
                <option value="pro">Professional (P*) Only</option>
                <option value="tech">Technical (T*) Only</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                Weekly Time Axis
              </Text>
              <Select
                size="xs"
                bg="white"
                value={weekAxisMode}
                onChange={(e) => {
                  setWeekAxisMode(e.target.value);
                  setMinWeekFilter('');
                  setMaxWeekFilter('');
                }}
              >
                <option value="dw">By DOS Week (Surgical)</option>
                <option value="rw">By EOB / Response Week</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                From Week
              </Text>
              <Select
                size="xs"
                bg="white"
                value={effectiveMinWeek}
                onChange={(e) => setMinWeekFilter(e.target.value)}
              >
                {allAvailableWeeks.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                To Week
              </Text>
              <Select
                size="xs"
                bg="white"
                value={effectiveMaxWeek}
                onChange={(e) => setMaxWeekFilter(e.target.value)}
              >
                {allAvailableWeeks.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
                Grid Layout / Top N
              </Text>
              <HStack spacing={1}>
                <Select
                  size="xs"
                  bg="white"
                  value={colsPerRow}
                  onChange={(e) => setColsPerRow(Number(e.target.value))}
                >
                  <option value={1}>1 / row</option>
                  <option value={2}>2 / row</option>
                  <option value={3}>3 / row</option>
                  <option value={4}>4 / row</option>
                </Select>
                <Select
                  size="xs"
                  bg="white"
                  value={topNCarriers}
                  onChange={(e) => setTopNCarriers(Number(e.target.value))}
                >
                  <option value={15}>Top 15</option>
                  <option value={20}>Top 20</option>
                  <option value={30}>Top 30</option>
                  <option value={40}>Top 40</option>
                  <option value={50}>Top 50</option>
                </Select>
              </HStack>
            </Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* 5 EXECUTIVE SUMMARY KPI CARDS */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} spacing={3}>
        <Card variant="outline" borderTop="3px solid" borderTopColor="blue.500">
          <CardBody py={3} px={3}>
            <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
              Total Claims in Scope
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="gray.800">
              {kpiTotals.sub.toLocaleString()}
            </Text>
            <Text fontSize="11px" color="gray.600">
              {kpiTotals.payersCount} Payers / {kpiTotals.carriersCount} Carriers
            </Text>
          </CardBody>
        </Card>

        <Card variant="outline" borderTop="3px solid" borderTopColor="red.500">
          <CardBody py={3} px={3}>
            <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
              Stage 1: {targetCpt} 277CA Edits
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="red.600">
              {kpiTotals.sub > 0 ? ((kpiTotals.chCpt / kpiTotals.sub) * 100).toFixed(1) : '0.0'}%
            </Text>
            <Text fontSize="11px" color="gray.600">
              {kpiTotals.chCpt.toLocaleString()} CPT-specific ({kpiTotals.chAny.toLocaleString()} any header)
            </Text>
          </CardBody>
        </Card>

        <Card variant="outline" borderTop="3px solid" borderTopColor="purple.500">
          <CardBody py={3} px={3}>
            <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
              Stage 2: {targetCpt} Record Holds
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="purple.600">
              {kpiTotals.sub > 0 ? ((kpiTotals.holdStrict / kpiTotals.sub) * 100).toFixed(1) : '0.0'}%
            </Text>
            <Text fontSize="11px" color="gray.600">
              {kpiTotals.holdStrict.toLocaleString()} strict ({kpiTotals.holdEob.toLocaleString()} EOB +{' '}
              {Math.max(kpiTotals.holdStrict - kpiTotals.holdEob, 0).toLocaleString()} letter)
            </Text>
          </CardBody>
        </Card>

        <Card variant="outline" borderTop="3px solid" borderTopColor="orange.500">
          <CardBody py={3} px={3}>
            <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
              Stage 3: {rowLabel} EOB Denied
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="orange.600">
              {kpiTotals.eobFin > 0 ? ((kpiTotals.eobDenFin / kpiTotals.eobFin) * 100).toFixed(1) : '0.0'}%
            </Text>
            <Text fontSize="11px" color="gray.600">
              {kpiTotals.eobDenFin.toLocaleString()} / {kpiTotals.eobFin.toLocaleString()} finalized {targetCpt} EOBs
            </Text>
          </CardBody>
        </Card>

        <Card variant="outline" borderTop="3px solid" borderTopColor="green.500">
          <CardBody py={3} px={3}>
            <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
              Stage 3: {rowLabel} EOB Approved
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="green.600">
              {kpiTotals.eobFin > 0 ? ((kpiTotals.eobPaid / kpiTotals.eobFin) * 100).toFixed(1) : '0.0'}%
            </Text>
            <Text fontSize="11px" color="gray.600">
              {kpiTotals.eobPaid.toLocaleString()} / {kpiTotals.eobFin.toLocaleString()} finalized {targetCpt} EOBs
            </Text>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* ================================================================
          STAGE 1: CLEARINGHOUSE REJECTIONS (277CA)
         ================================================================ */}
      {(stageViewFilter === 'ALL_STAGES' || stageViewFilter === 'STAGE_1') && (
        <Box>
          <Heading size="sm" color="red.700" mb={2}>
            📌 Stage 1: Clearinghouse Rejections — {cptLabel}-Specific 277CA Edits & Rejections (% by Week)
          </Heading>
          <Alert status="info" variant="left-accent" borderRadius="md" mb={3} py={2.5} bg="blue.50">
            <AlertIcon />
            <Box fontSize="xs" color="gray.800">
              <Text fontWeight="bold" mb={0.5}>
                What is 277CA? When a claim (837P) is submitted electronically, the clearinghouse and payer gateway return an automated 277CA Claim Acknowledgment within 24–48 hours.
              </Text>
              <Text>
                • <b>Default Mode ({cptLabel}-Specific 277CA Edits Only):</b> Isolates clearinghouse rejections/edits <b>specifically triggered by the {rowLabel}</b> (e.g., missing <Code fontSize="10px">SV101-07</Code> unlisted narrative description on 95999, <Code fontSize="10px">P4999UUCD</Code> SmartEdit unlisted reject, or a line-level <Code fontSize="10px">SVC</Code> Reason Code 167 edit), <b>excluding</b> generic NPI / Tax ID / Subscriber ID claim-header blocks.
                <br />• <b>How to read the graphs:</b> <b>Top Red Line (0–100%)</b> = % of submitted claims rejected/edited on 277CA for this scope that week. <b>Bottom Bars</b> = Total claims submitted that week.
              </Text>
            </Box>
          </Alert>

          {targetCpt !== 'ALL_CLAIMS' && (
            <HStack spacing={2} mb={3} wrap="wrap">
              <Text fontSize="xs" fontWeight="bold" color="gray.700">
                Stage 1 ({targetCpt}) Scope:
              </Text>
              <ButtonGroup size="xs" isAttached variant="outline">
                <Button
                  colorScheme={stage1Submode === 'CPT_SPECIFIC_ONLY' ? 'red' : 'gray'}
                  variant={stage1Submode === 'CPT_SPECIFIC_ONLY' ? 'solid' : 'outline'}
                  onClick={() => setStage1Submode('CPT_SPECIFIC_ONLY')}
                >
                  {cptLabel}-Specific 277CA Edits Only (Excludes NPI/Tax ID/Subscriber Header Blocks) [Default]
                </Button>
                <Button
                  colorScheme={stage1Submode === 'ALL_HEADER_REJECTIONS' ? 'red' : 'gray'}
                  variant={stage1Submode === 'ALL_HEADER_REJECTIONS' ? 'solid' : 'outline'}
                  onClick={() => setStage1Submode('ALL_HEADER_REJECTIONS')}
                >
                  All Claim-Header 277CA Rejections (Includes NPI/Tax ID/Subscriber Blocks)
                </Button>
              </ButtonGroup>
            </HStack>
          )}

          {entityLevelMode !== 'CARRIERS_ONLY' && (
            <Box mb={5}>
              <Heading size="xs" color="gray.700" mb={2}>
                A. By Payer Group — {stage1RateLabel} (Sorted by Total Volume Desc)
              </Heading>
              <SimpleGrid columns={{ base: 1, md: Math.min(colsPerRow, 2), lg: colsPerRow }} spacing={3}>
                {stage1Grids.payerEntities.map((ent, idx) => (
                  <StageSmallMultipleCard
                    key={`s1_pg_${ent.entityName}`}
                    rank={idx + 1}
                    entityName={ent.entityName}
                    totalDenom={ent.totalDenom}
                    totalNum={ent.totalNum}
                    totalNum2={null}
                    weeklySeries={ent.weeklySeries}
                    rateLabel={stage1RateLabel}
                    rate2Label={null}
                    lineColor="#DC2626"
                    line2Color={null}
                    barColor="#94A3B8"
                    rowLabel={rowLabel}
                    stageId="s1"
                    groupType="pg"
                  />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {entityLevelMode !== 'PAYERS_ONLY' && (
            <Box>
              <Heading size="xs" color="gray.700" mb={2}>
                B. Top {topNCarriers} Specific Carriers by Volume — {stage1RateLabel} (Sorted by Total Volume Desc)
              </Heading>
              <SimpleGrid columns={{ base: 1, md: Math.min(colsPerRow, 2), lg: colsPerRow }} spacing={3}>
                {stage1Grids.carrierEntities.map((ent, idx) => (
                  <StageSmallMultipleCard
                    key={`s1_cr_${ent.entityName}`}
                    rank={idx + 1}
                    entityName={ent.entityName}
                    totalDenom={ent.totalDenom}
                    totalNum={ent.totalNum}
                    totalNum2={null}
                    weeklySeries={ent.weeklySeries}
                    rateLabel={stage1RateLabel}
                    rate2Label={null}
                    lineColor="#DC2626"
                    line2Color={null}
                    barColor="#94A3B8"
                    rowLabel={rowLabel}
                    stageId="s1"
                    groupType="cr"
                  />
                ))}
              </SimpleGrid>
            </Box>
          )}
          <Divider mt={6} />
        </Box>
      )}

      {/* ================================================================
          STAGE 2: MEDICAL RECORD REQUESTS / PRE-PAY HOLDS
         ================================================================ */}
      {(stageViewFilter === 'ALL_STAGES' || stageViewFilter === 'STAGE_2') && (
        <Box>
          <Heading size="sm" color="purple.700" mb={2}>
            📌 Stage 2: Medical Record Requests / Pre-Pay Holds — {cptLabel}-Specific Record Holds (% by Week)
          </Heading>
          <Alert status="info" variant="left-accent" borderRadius="md" mb={3} py={2.5} bg="purple.50" borderColor="purple.500">
            <AlertIcon color="purple.600" />
            <Box fontSize="xs" color="gray.800">
              <Text fontWeight="bold" mb={0.5}>
                What is a Pre-Pay Record Hold? The claim passed the clearinghouse gate, but the payer suspended payment and requested clinical medical records (Operative Report, IONM log, or physician order).
              </Text>
              <Text>
                • <b>Default Mode (Strict {cptLabel}-Specific Holds):</b> Counts a claim <b>only</b> when (a) the EOB stamped <Code fontSize="10px">CO-252 / PI-252 / M127 / N350</Code> <b>directly on the {rowLabel}</b>, <b>OR</b> (b) the mailed <b>Lockbox Medical Records Request letter (tagId=162) explicitly listed {targetCpt} in cpt_codes_mentioned</b>, <b>excluding</b> claims where records were requested only for other companion CPTs.
                <br />• <b>How to read the graphs:</b> <b>Top Purple Line (0–100%)</b> = % of submitted claims held for medical records on the {rowLabel} that week. <b>Bottom Bars</b> = Total claims submitted that week.
              </Text>
            </Box>
          </Alert>

          {targetCpt !== 'ALL_CLAIMS' && (
            <HStack spacing={2} mb={3} wrap="wrap">
              <Text fontSize="xs" fontWeight="bold" color="gray.700">
                Stage 2 ({targetCpt}) Scope:
              </Text>
              <ButtonGroup size="xs" isAttached variant="outline" flexWrap="wrap">
                <Button
                  colorScheme={stage2Submode === 'STRICT_CPT_HOLD' ? 'purple' : 'gray'}
                  variant={stage2Submode === 'STRICT_CPT_HOLD' ? 'solid' : 'outline'}
                  onClick={() => setStage2Submode('STRICT_CPT_HOLD')}
                >
                  Strict {targetCpt}-Specific Holds (EOB {targetCpt} Line + Lockbox Naming {targetCpt}) [Default]
                </Button>
                <Button
                  colorScheme={stage2Submode === 'EOB_LINE_HOLD_ONLY' ? 'purple' : 'gray'}
                  variant={stage2Submode === 'EOB_LINE_HOLD_ONLY' ? 'solid' : 'outline'}
                  onClick={() => setStage2Submode('EOB_LINE_HOLD_ONLY')}
                >
                  EOB {targetCpt} Line Hold Only (CO-252 / M127)
                </Button>
                <Button
                  colorScheme={stage2Submode === 'INCL_WHOLE_CLAIM_LETTER' ? 'purple' : 'gray'}
                  variant={stage2Submode === 'INCL_WHOLE_CLAIM_LETTER' ? 'solid' : 'outline'}
                  onClick={() => setStage2Submode('INCL_WHOLE_CLAIM_LETTER')}
                >
                  Include Whole-Claim Lockbox Letters (Excludes Other-CPT-Only Holds)
                </Button>
                <Button
                  colorScheme={stage2Submode === 'ALL_CLAIM_HOLDS' ? 'purple' : 'gray'}
                  variant={stage2Submode === 'ALL_CLAIM_HOLDS' ? 'solid' : 'outline'}
                  onClick={() => setStage2Submode('ALL_CLAIM_HOLDS')}
                >
                  All Claim-Level Record Holds (Unfiltered)
                </Button>
              </ButtonGroup>
            </HStack>
          )}

          {entityLevelMode !== 'CARRIERS_ONLY' && (
            <Box mb={5}>
              <Heading size="xs" color="gray.700" mb={2}>
                A. By Payer Group — {stage2RateLabel} (Sorted by Total Volume Desc)
              </Heading>
              <SimpleGrid columns={{ base: 1, md: Math.min(colsPerRow, 2), lg: colsPerRow }} spacing={3}>
                {stage2Grids.payerEntities.map((ent, idx) => (
                  <StageSmallMultipleCard
                    key={`s2_pg_${ent.entityName}`}
                    rank={idx + 1}
                    entityName={ent.entityName}
                    totalDenom={ent.totalDenom}
                    totalNum={ent.totalNum}
                    totalNum2={null}
                    weeklySeries={ent.weeklySeries}
                    rateLabel={stage2RateLabel}
                    rate2Label={null}
                    lineColor="#7C3AED"
                    line2Color={null}
                    barColor="#A78BFA"
                    rowLabel={rowLabel}
                    stageId="s2"
                    groupType="pg"
                  />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {entityLevelMode !== 'PAYERS_ONLY' && (
            <Box>
              <Heading size="xs" color="gray.700" mb={2}>
                B. Top {topNCarriers} Specific Carriers by Volume — {stage2RateLabel} (Sorted by Total Volume Desc)
              </Heading>
              <SimpleGrid columns={{ base: 1, md: Math.min(colsPerRow, 2), lg: colsPerRow }} spacing={3}>
                {stage2Grids.carrierEntities.map((ent, idx) => (
                  <StageSmallMultipleCard
                    key={`s2_cr_${ent.entityName}`}
                    rank={idx + 1}
                    entityName={ent.entityName}
                    totalDenom={ent.totalDenom}
                    totalNum={ent.totalNum}
                    totalNum2={null}
                    weeklySeries={ent.weeklySeries}
                    rateLabel={stage2RateLabel}
                    rate2Label={null}
                    lineColor="#7C3AED"
                    line2Color={null}
                    barColor="#A78BFA"
                    rowLabel={rowLabel}
                    stageId="s2"
                    groupType="cr"
                  />
                ))}
              </SimpleGrid>
            </Box>
          )}
          <Divider mt={6} />
        </Box>
      )}

      {/* ================================================================
          STAGE 3: UNIFIED EOB ADJUDICATION OUTCOMES (DENIED % VS. APPROVED %)
         ================================================================ */}
      {(stageViewFilter === 'ALL_STAGES' || stageViewFilter === 'STAGE_3') && (
        <Box>
          <Heading size="sm" color="orange.700" mb={2}>
            📌 Stage 3: EOB Adjudication Outcomes (Specific to {rowLabel}) — {rowLabel} EOB Denied % vs. {rowLabel} EOB Approved % (by Week)
          </Heading>
          <Alert status="info" variant="left-accent" borderRadius="md" mb={3} py={2.5} bg="orange.50" borderColor="orange.500">
            <AlertIcon color="orange.600" />
            <Box fontSize="xs" color="gray.800">
              <Text fontWeight="bold" mb={0.5}>
                Strictly Specific to the {rowLabel} on the EOB: Both percentages below evaluate ONLY the {rowLabel} on the Explanation of Benefits (EOB / 835 ERA)—even if other companion CPT codes on the same claim (95938, 95939, 95861, G0453) were paid.
              </Text>
              <Text>
                • <b>Default Scope (Finalized EOB Decisions Only):</b> Includes all claims where the <b>{rowLabel}</b> received a <b>final EOB decision</b> ({rowLabel} Approved &gt; $0 or {rowLabel} Substantively Denied = $0), <b>excluding</b> EOBs where the {rowLabel} is still sitting in a Stage 2 <Code fontSize="10px">CO-252 / M127</Code> pending record hold (so Stage 2 holds are not double-counted as Stage 3 denials).
                <br />• <b>How to read the graphs:</b> <b>🟠 Orange Line (0–100%)</b> = <b>{rowLabel} EOB Denied %</b> ($0.00 allowed/paid on the {rowLabel}). <b>🟢 Green Line (0–100%)</b> = <b>{rowLabel} EOB Approved %</b> (Allowed/Paid &gt; $0.00 on the {rowLabel}). <b>Bottom Bars</b> = Weekly claims with a finalized EOB decision on the {rowLabel} (<b>Denied % + Approved % = 100%</b>).
              </Text>
            </Box>
          </Alert>

          <HStack spacing={2} mb={3} wrap="wrap">
            <Text fontSize="xs" fontWeight="bold" color="gray.700">
              Stage 3 ({rowLabel}) EOB Denominator Scope:
            </Text>
            <ButtonGroup size="xs" isAttached variant="outline" flexWrap="wrap">
              <Button
                colorScheme={stage3Submode === 'FINALIZED_EOBS_ONLY' ? 'orange' : 'gray'}
                variant={stage3Submode === 'FINALIZED_EOBS_ONLY' ? 'solid' : 'outline'}
                onClick={() => setStage3Submode('FINALIZED_EOBS_ONLY')}
              >
                Finalized {rowLabel} EOB Decisions Only — Excludes Pending CO-252/M127 Record-Hold EOBs ({rowLabel} Denied % + Approved % = 100%) [Default]
              </Button>
              <Button
                colorScheme={stage3Submode === 'ALL_EOBS_INCL_252_HOLDS' ? 'orange' : 'gray'}
                variant={stage3Submode === 'ALL_EOBS_INCL_252_HOLDS' ? 'solid' : 'outline'}
                onClick={() => setStage3Submode('ALL_EOBS_INCL_252_HOLDS')}
              >
                All {rowLabel} EOBs — Treats Pending CO-252/M127 Record-Hold EOBs as $0 Denied
              </Button>
            </ButtonGroup>
          </HStack>

          {entityLevelMode !== 'CARRIERS_ONLY' && (
            <Box mb={5}>
              <Heading size="xs" color="gray.700" mb={2}>
                A. By Payer Group — {stage3RateLabel} vs. {stage3Rate2Label} (Sorted by Total Volume Desc)
              </Heading>
              <SimpleGrid columns={{ base: 1, md: Math.min(colsPerRow, 2), lg: colsPerRow }} spacing={3}>
                {stage3Grids.payerEntities.map((ent, idx) => (
                  <StageSmallMultipleCard
                    key={`s3_pg_${ent.entityName}`}
                    rank={idx + 1}
                    entityName={ent.entityName}
                    totalDenom={ent.totalDenom}
                    totalNum={ent.totalNum}
                    totalNum2={ent.totalNum2}
                    weeklySeries={ent.weeklySeries}
                    rateLabel={stage3RateLabel}
                    rate2Label={stage3Rate2Label}
                    lineColor="#D97706"
                    line2Color="#059669"
                    barColor="#CBD5E1"
                    rowLabel={rowLabel}
                    stageId="s3"
                    groupType="pg"
                  />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {entityLevelMode !== 'PAYERS_ONLY' && (
            <Box>
              <Heading size="xs" color="gray.700" mb={2}>
                B. Top {topNCarriers} Specific Carriers by Volume — {stage3RateLabel} vs. {stage3Rate2Label} (Sorted by Total Volume Desc)
              </Heading>
              <SimpleGrid columns={{ base: 1, md: Math.min(colsPerRow, 2), lg: colsPerRow }} spacing={3}>
                {stage3Grids.carrierEntities.map((ent, idx) => (
                  <StageSmallMultipleCard
                    key={`s3_cr_${ent.entityName}`}
                    rank={idx + 1}
                    entityName={ent.entityName}
                    totalDenom={ent.totalDenom}
                    totalNum={ent.totalNum}
                    totalNum2={ent.totalNum2}
                    weeklySeries={ent.weeklySeries}
                    rateLabel={stage3RateLabel}
                    rate2Label={stage3Rate2Label}
                    lineColor="#D97706"
                    line2Color="#059669"
                    barColor="#CBD5E1"
                    rowLabel={rowLabel}
                    stageId="s3"
                    groupType="cr"
                  />
                ))}
              </SimpleGrid>
            </Box>
          )}
        </Box>
      )}
    </VStack>
  );
});

export default NMAVisualDiscoveryTab;
